import { useState } from "react";
import { ago, api, can, Empty, Loading, Pill, useLoad, useMe } from "./lib";

export const ISSUE_STATUS: Record<string, { label: string; tone: string }> = {
  open: { label: "Open", tone: "" },
  in_review: { label: "PR in review", tone: "info" },
  fixed_in_pr: { label: "Fix merged", tone: "accent" },
  done: { label: "Done", tone: "good" },
  not_planned: { label: "Not planned", tone: "" },
};

export function MilestoneBar({ m }: { m: any }) {
  const total = m.open_issues + m.closed_issues;
  const pct = total ? Math.round((m.closed_issues / total) * 100) : 0;
  return (
    <div className="stack" style={{ gap: 4 }}>
      <div className="progress"><div style={{ width: `${pct}%` }} /></div>
      <span className="muted tiny">{m.closed_issues} of {total} closed on GitHub{m.counts?.in_review ? ` · ${m.counts.in_review} in review` : ""}{m.due_on ? ` · due ${m.due_on}` : ""}</span>
    </div>
  );
}

export function GitHubView() {
  const { me, toast } = useMe();
  const { data, error, reload } = useLoad<any>("/api/github");
  const [theme, setTheme] = useState("");
  const [milestone, setMilestone] = useState<string>("");
  const [status, setStatus] = useState("active");
  const [busy, setBusy] = useState(false);
  if (!data) return <Loading error={error} />;
  const integ = data.integration;
  const issues = data.issues.filter(
    (i: any) =>
      (!theme || i.themes.includes(theme)) &&
      (!milestone || String(i.milestone_number ?? "none") === milestone) &&
      (status === "all" ? true : status === "active" ? ["open", "in_review", "fixed_in_pr"].includes(i.status) : i.status === status),
  );
  const byMilestone = new Map<string, any[]>();
  for (const i of issues) {
    const k = String(i.milestone_number ?? "none");
    byMilestone.set(k, [...(byMilestone.get(k) ?? []), i]);
  }
  const msName = (k: string) => (k === "none" ? "No milestone" : data.milestones.find((m: any) => String(m.number) === k)?.title ?? `Milestone ${k}`);

  return (
    <div className="stack" style={{ gap: 16 }}>
      <section className="card row between">
        <div className="stack" style={{ gap: 2 }}>
          <strong>GitHub {integ?.last_detail?.repo ? `· ${integ.last_detail.repo}` : ""}</strong>
          <span className="muted small">
            {integ?.status === "ok" ? `Read-only. Last synced ${ago(integ.last_success_at)}; checks every 30 minutes.` : integ?.status === "error" ? integ.last_error : "Not connected yet. Needs a read-only token (see the setup steps)."}
          </span>
        </div>
        {(can(me, "product.edit") || can(me, "metrics.edit")) && (
          <button className="btn sm" disabled={busy} onClick={async () => { setBusy(true); try { const r = await api("/api/github/sync", { method: "POST" }); toast(r.result === "ok" ? "Synced with GitHub" : r.integration?.last_error ?? "Not connected", r.result !== "ok"); reload(); } catch (e: any) { toast(e.message, true); } finally { setBusy(false); } }}>
            {busy ? "Syncing…" : "Sync now"}
          </button>
        )}
      </section>

      {data.milestones.filter((m: any) => m.state === "open").length > 0 && (
        <div className="grid">
          {data.milestones.filter((m: any) => m.state === "open").map((m: any) => (
            <section key={m.number} className="card stack clickable" onClick={() => setMilestone(milestone === String(m.number) ? "" : String(m.number))} style={milestone === String(m.number) ? { borderColor: "var(--accent)" } : undefined}>
              <div className="row between"><h2>{m.title}</h2>{m.release_ids.length ? <Pill tone="good">Linked to release</Pill> : <Pill>No portal release yet</Pill>}</div>
              <MilestoneBar m={m} />
            </section>
          ))}
        </div>
      )}

      <div className="row">
        <select className="input" style={{ width: "auto" }} value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
          <option value="active">Not done yet</option>
          {Object.entries(ISSUE_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          <option value="all">Everything</option>
        </select>
        <select className="input" style={{ width: "auto" }} value={milestone} onChange={(e) => setMilestone(e.target.value)} aria-label="Release">
          <option value="">All releases</option>
          {data.milestones.map((m: any) => <option key={m.number} value={String(m.number)}>{m.title}</option>)}
          <option value="none">No milestone</option>
        </select>
        <div className="row" style={{ gap: 6 }}>
          {data.themes.map((t: string) => <button key={t} className={`pill ${theme === t ? "accent" : ""}`} style={{ cursor: "pointer" }} onClick={() => setTheme(theme === t ? "" : t)}>{t}</button>)}
          {!data.themes.length && integ?.status === "ok" && <span className="muted tiny">No theme labels yet. Add labels like "theme: onboarding" on GitHub.</span>}
        </div>
      </div>

      {!issues.length ? (
        <Empty>{integ?.status === "ok" ? "No issues match these filters." : "Nothing synced yet."}</Empty>
      ) : (
        [...byMilestone.entries()].map(([k, list]) => (
          <section key={k} className="card" style={{ padding: "8px 16px" }}>
            <h2 style={{ margin: "10px 0 4px" }}>{msName(k)} <span className="muted small">{list.length}</span></h2>
            <div className="list">
              {list.map((i: any) => (
                <a key={i.number} href={i.url} target="_blank" rel="noreferrer" className="item clickable" style={{ color: "inherit", textDecoration: "none" }}>
                  <span className="muted small" style={{ width: 54 }}>#{i.number}</span>
                  <span className="grow">
                    <span className="title">{i.title}</span>
                    <span className="small muted" style={{ display: "block" }}>
                      {[...i.themes, ...i.assignees.map((a: string) => `@${a}`)].join(" · ")}
                      {i.prs.length ? ` · ${i.prs.map((p: any) => `PR #${p.number} ${p.state}`).join(", ")}` : ""}
                    </span>
                  </span>
                  {i.needs_kayla && <Pill tone="warn">Needs Kayla</Pill>}
                  <Pill tone={ISSUE_STATUS[i.status]?.tone}>{ISSUE_STATUS[i.status]?.label}</Pill>
                </a>
              ))}
            </div>
          </section>
        ))
      )}
      <p className="muted tiny" style={{ margin: 0 }}>Read-only: titles, labels, status and links. Nothing here changes GitHub. Milestones match portal releases by version (milestone "1.2.0" ↔ release 1.2.0).</p>
    </div>
  );
}
